/**
 * API Configuration
 * 
 * CONFIG_SETTINGS values:
 * - 5: Local development (uses full localhost URLs)
 * - 4: Production/Shortened (uses relative paths for same-domain API)
 */

const CONFIG_SETTINGS = process.env.REACT_APP_CONFIG_SETTINGS
	? parseInt(process.env.REACT_APP_CONFIG_SETTINGS, 10)
	: null;

function isBrowserLocalhost() {
	if (typeof window === 'undefined') return false;
	return ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
}

/**
 * Get the base URL for API calls based on configuration
 * @returns {string} The base URL for API endpoints
 */
export function getApiBaseUrl() {
	if (process.env.REACT_APP_API_URL) {
		return process.env.REACT_APP_API_URL;
	}

	if (CONFIG_SETTINGS === 5) {
		return 'http://localhost:8000';
	} else if (CONFIG_SETTINGS === 4) {
		return '/api/v1';
	}

	return isBrowserLocalhost() ? 'http://localhost:8000' : '/api/v1';
}

/**
 * Build a full API endpoint URL
 * @param {string} path - The API path (e.g., '/crypto/keygen', '/firmware/sign')
 * @returns {string} The full URL
 * 
 * Examples:
 * - Local (CONFIG=5): buildApiUrl('/crypto/keygen') → 'http://localhost:8000/crypto/keygen'
 * - Production (CONFIG=4): buildApiUrl('/crypto/keygen') → '/api/v1/crypto/keygen'
 */
export function buildApiUrl(path) {
	const baseUrl = getApiBaseUrl();
	
	// Ensure path starts with /
	const normalizedPath = path.startsWith('/') ? path : `/${path}`;
	
	// Combine base URL with path
	return `${baseUrl}${normalizedPath}`;
}

/**
 * Get current configuration mode
 * @returns {number} The CONFIG_SETTINGS value
 */
export function getConfigMode() {
	return CONFIG_SETTINGS;
}

/**
 * Check if running in local mode
 * @returns {boolean}
 */
export function isLocalMode() {
	return CONFIG_SETTINGS === 5 || (CONFIG_SETTINGS === null && isBrowserLocalhost());
}

/**
 * Check if running in production/shortened mode
 * @returns {boolean}
 */
export function isProductionMode() {
	return CONFIG_SETTINGS === 4 || (CONFIG_SETTINGS === null && !isBrowserLocalhost());
}

const apiConfig = {
	getApiBaseUrl,
	buildApiUrl,
	getConfigMode,
	isLocalMode,
	isProductionMode,
};

export default apiConfig;
